/**
 * The Toado widget loader.
 *
 * This is the one line a customer pastes:
 *
 *   <script src="https://cdn.toado.dev/widget/v1/loader.js"
 *           data-toado-key="wgt_live_..." async></script>
 *
 * Responsibilities, and nothing else:
 *   1. Find its own script tag and read the key.
 *   2. Refuse to run where it cannot work (unsupported browser, duplicate
 *      embed, inside its own iframe).
 *   3. Queue any `window.Toado.*` calls the page makes before the real
 *      bundle arrives, so the host page never has to wait for us.
 *   4. Start hearing the page's console and errors (TOADO-910).
 *   5. Lazily fetch the main bundle.
 *
 * Everything expensive lives in the main bundle. A page that never opens
 * the widget pays for this file and one cached config request.
 */

import { dropPageLogs, leavePageLogs, PAGE_LOGS_HANDOFF, pageLogsLeft } from './capture/pageLogs';
import { onDeveloperHost } from './developerHost';

(function bootstrap(): void {
  // A second copy of the loader on the same page would render two
  // launchers and double every report. Cheap to guard, easy to hit on
  // sites that inject scripts through a tag manager.
  if (window.__toadoLoaderRan) return;
  // The bundle is on the page already, included directly: it is listening,
  // and the bundle this would fetch stops at its own guard, so listeners
  // started here would have no owner (TOADO-910, Codex round 1).
  if (window.__toadoWidgetLoaded) return;
  // The page took the widget down; a later tag does not bring it back.
  if (window.__toadoWidgetCancelled) return;

  // `document.currentScript` is null inside async module evaluation, so
  // fall back to locating the tag by its data attribute.
  const script = (document.currentScript as HTMLScriptElement | null)
    ?? document.querySelector<HTMLScriptElement>('script[data-toado-key]');
  if (!script) return;
  // Taken off the page before it ran, as the Toado app takes its own widget
  // off for a session that is not staff: a removed script still runs once the
  // browser has it, so it starts nothing itself (TOADO-910, Codex round 2).
  if (!script.isConnected) return;

  const key = script.getAttribute('data-toado-key');
  if (!key) {
    // A missing key is the most likely install mistake, so it is worth
    // saying — but only where a developer is looking. Warning on every
    // visitor's console pollutes a production site we are a guest on, and
    // the Studio's verification panel is where an owner actually finds
    // out the snippet is not working.
    if (onDeveloperHost()) {
      console.warn('[toado] widget script is missing data-toado-key');
    }
    return;
  }

  // Feature gate. These are the APIs the capture path cannot work
  // without; on anything older we simply do not render, rather than
  // shipping a broken button.
  const supported = typeof window.fetch === 'function'
    && typeof window.Promise === 'function'
    && typeof document.createElement('div').attachShadow === 'function'
    && typeof FormData !== 'undefined';
  if (!supported) return;

  // The widget renders its own dialog in the top-level document. If the
  // customer embeds their site inside an iframe we still run, but we skip
  // the case where our OWN hosted page is the frame — otherwise the
  // public-link page would boot a second widget inside itself.
  if (window.name === 'toado-widget-frame') return;

  // Where the bundle and the API are, worked out before anything is started:
  // a tag the address cannot be read from (an inline script carrying the key,
  // say) would otherwise throw here and leave listeners with no owner
  // (TOADO-910, z.ai round 1).
  let apiBase: string;
  let bundleUrl: string;
  try {
    apiBase = script.getAttribute('data-toado-api')
      // Derive from the script's own origin so a self-hosted or
      // CDN-fronted deployment does not need a second attribute.
      ?? new URL(script.src, window.location.href).origin;
    bundleUrl = new URL('toado-widget.js', script.src).href;
  } catch {
    return;
  }

  // Claim the guard only now that we have a usable install, just before the
  // first thing this does to the page. Setting it at the top meant a malformed
  // tag earlier in the document permanently suppressed a correct one further
  // down, and so did setting it before the checks above (z.ai, round 2).
  //
  // The claim is this load's own object, as are the listeners and the stub
  // below, so a failed load releases only what is still its own: after the
  // Toado app takes its widget down and puts it back for staff, a newer
  // loader holds the guard and the listeners, and an old bundle failing late
  // must not take them from it (Codex, round 7).
  const claim = {};
  try {
    window.__toadoLoaderRan = claim;
  } catch {
    return; // frozen window; nothing further will work either
  }

  // Hear the page's console and errors from now, not from when the bundle
  // runs after the browser goes idle (TOADO-910): what a page logs and throws
  // while starting up is most of what a report needs, and neither is replayed
  // to a late listener the way past requests are. A page that started them
  // itself (the Toado app does, before its own code) keeps those. The bundle
  // takes them over; if it never loads, they are stopped below.
  // What this run made, as opposed to what it found and borrowed: only that
  // is its to take away if its bundle fails (z.ai, round 8).
  const madeLogs = !pageLogsLeft();
  if (madeLogs) leavePageLogs();
  const handoff = (window as unknown as Record<string, unknown>)[PAGE_LOGS_HANDOFF];
  let madeStub = false;

  // Stub `window.Toado` immediately and queue calls. Without this, a host
  // page calling `Toado.open()` from its own "Report a bug" menu item
  // would throw whenever the click beat the bundle.
  //
  // A page can have put anything at `window.Toado`; one this cannot build on
  // gets a fresh stub, rather than throwing here with the listeners already
  // on and nothing to take them off (z.ai, round 3).
  let pending: ToadoPendingApi;
  try {
    const found = window.Toado as ToadoPendingApi | undefined;
    madeStub = !(found && typeof found === 'object');
    pending = madeStub ? { q: [] } : found!;
    if (!Array.isArray(pending.q)) pending.q = [];
    for (const method of ['open', 'close', 'capture', 'identify', 'setTheme', 'mount', 'unmount', 'setView', 'setContext']) {
      if (typeof pending[method] !== 'function') {
        pending[method] = (...args: unknown[]) => {
          pending.q.push({ method, args });
        };
      }
    }
    // Destroy before the bundle comes is queued for it, which then starts
    // nothing, and takes the listeners off at once (z.ai, round 3). It also
    // marks the page, because the queue does not last: a bundle that fails
    // to load takes the stub away, and a copy that arrives after that would
    // find no destroy to obey (Codex, round 5).
    //
    // Only while this stub is the page's: a destroy kept from a stub the
    // Toado app has since replaced, when it put its widget back for staff,
    // must not cancel the widget that replaced it (Codex, round 8).
    if (typeof pending.destroy !== 'function') {
      pending.destroy = (...args: unknown[]) => {
        if (window.Toado !== pending) return;
        pending.q.push({ method: 'destroy', args });
        dropPageLogs();
        try { window.__toadoWidgetCancelled = true; } catch { /* frozen window */ }
      };
    }
  } catch {
    pending = { q: [] };
    madeStub = true;
  }
  try {
    window.Toado = pending;
  } catch {
    // Read-only global or frozen window. The bundle still loads and the
    // launcher still renders; only the programmatic API is unavailable.
  }

  const el = document.createElement('script');
  el.src = bundleUrl;
  el.async = true;
  el.setAttribute('data-toado-key', key);
  el.setAttribute('data-toado-api', apiBase);
  // "Try it live" in the Toado app (TOADO-894): the trial the page loads.
  const trial = script.getAttribute('data-toado-trial');
  if (trial) el.setAttribute('data-toado-trial', trial);
  el.onerror = () => {
    // Only while the page is still this run's. Another run holding the guard
    // may be using this run's listeners and stub, as a loader does when a page
    // lifted the guard alone, so nothing of them is this run's to take
    // (z.ai, round 7). And only what this run made: listeners or a stub it
    // found belong to a run whose bundle may still be on its way (round 8).
    const guard = window.__toadoLoaderRan;
    if (!guard || guard === claim) {
      // Nothing will take the listeners over, so they come off the page, and
      // off the window, before the warning below would be recorded by them.
      if (madeLogs && (window as unknown as Record<string, unknown>)[PAGE_LOGS_HANDOFF] === handoff) dropPageLogs();
      // Nor will anything take the stub's calls; with the guard released, a
      // later tag can try again (z.ai, round 3). The guard first, each on its
      // own: a `Toado` the page made undeletable must not keep the guard held
      // (z.ai, round 4).
      try { if (guard) delete window.__toadoLoaderRan; } catch { /* a frozen window keeps it */ }
      try { if (madeStub && window.Toado === pending) delete window.Toado; } catch { /* nor this */ }
    }
    if (onDeveloperHost()) {
      console.warn('[toado] failed to load widget bundle from', bundleUrl);
    }
  };

  // Defer to idle so the widget never competes with the host page's own
  // critical path. `requestIdleCallback` is not universal; the timeout
  // fallback keeps behavior consistent.
  const append = () => document.head.appendChild(el);
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(append, { timeout: 3000 });
  } else {
    setTimeout(append, 1);
  }
}());
